import "./mark-prompts.css";
import { PRODUCT, WEB_ROUTES } from "./config";
import {
  CloudflareMarkPromptsRepository,
  type MarkPrompt,
  type MarkPromptDay,
  type MarkPromptOrder,
  type MarkPromptReviewState,
  type MarkPromptSource,
  type MarkPromptsPageData,
  type MarkPromptsRepository,
} from "./mark-prompts";

const reviewLabels: Record<MarkPromptReviewState, string> = {
  unreviewed: "Unreviewed",
  triaged: "Triaged",
  actioned: "Actioned",
  duplicate: "Duplicate",
  no_action: "No action",
  obsolete: "Obsolete",
};

const reviewStates = Object.keys(reviewLabels) as MarkPromptReviewState[];

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const value = document.createElement(name);
  if (className) value.className = className;
  if (text !== undefined) value.textContent = text;
  return value;
}

function formatTimestamp(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function chartLabel(day: MarkPromptDay): string {
  const date = new Date(`${day.date}T12:00:00`);
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(date);
}

export class MarkPromptsPage {
  private readonly repository: MarkPromptsRepository;
  private prompts: MarkPrompt[] = [];
  private sources: MarkPromptSource[] = [];
  private selectedTaskIDs = new Set<string>();
  private stats: MarkPromptsPageData["stats"] = { total: 0, byDay: [] };
  private nextOffset: number | null = null;
  private order: MarkPromptOrder = "desc";
  private searchQuery = "";
  private selectedStates = new Set<MarkPromptReviewState>(reviewStates);
  private loadingOlder = false;
  private loadGeneration = 0;

  constructor(
    private readonly root: HTMLElement,
    dependencies: {
      repository?: MarkPromptsRepository;
    } = {},
  ) {
    this.repository = dependencies.repository
      ?? new CloudflareMarkPromptsRepository();
  }

  async start(): Promise<void> {
    const generation = ++this.loadGeneration;
    this.loadingOlder = false;
    document.documentElement.dataset.surface = "mark-prompts";
    document.title = `Mark prompts · ${PRODUCT.name}`;
    this.renderLoading();
    try {
      const page = await this.repository.load();
      if (generation !== this.loadGeneration) return;
      this.prompts = page.prompts;
      this.stats = page.stats;
      this.sources = page.sources;
      this.selectedTaskIDs = new Set(
        page.sources.map(({ taskID }) => taskID),
      );
      this.order = page.order;
      this.nextOffset = page.nextOffset;
      this.renderDashboard();
    } catch {
      if (generation !== this.loadGeneration) return;
      this.renderUnavailable();
    }
  }

  private gate(eyebrow: string, title: string, copy: string): HTMLElement {
    const main = element("main", "gate-shell");
    main.id = "app-content";
    main.tabIndex = -1;
    const card = element("section", "gate-card");
    const mark = element("span", "brand-mark", PRODUCT.brandMark);
    mark.setAttribute("aria-hidden", "true");
    card.append(
      mark,
      element("p", "eyebrow", eyebrow),
      element("h1", undefined, title),
      element("p", "gate-copy", copy),
    );
    main.append(card);
    return main;
  }

  private renderLoading(): void {
    this.root.replaceChildren(this.gate(
      "A public build diary",
      "Opening Mark prompts…",
      "Loading Mark’s questions, ideas, and the work that followed.",
    ));
  }

  private renderUnavailable(): void {
    const main = this.gate(
      "Build diary",
      "Mark prompts is temporarily unavailable.",
      "The public timeline could not be loaded. Nothing else on Convoful is affected.",
    );
    const card = main.querySelector<HTMLElement>(".gate-card");
    const retry = element("button", "primary-button gate-primary-action", "Try again");
    retry.type = "button";
    retry.addEventListener("click", () => void this.start());
    card?.append(retry);
    this.root.replaceChildren(main);
  }

  private renderDashboard(
    announcement?: string,
    focusSearch = false,
  ): void {
    const main = element("main", "mark-prompts-shell");
    main.id = "app-content";
    main.tabIndex = -1;

    const header = element("header", "mark-prompts-header");
    const brand = element("a", "mark-prompts-brand");
    brand.href = WEB_ROUTES.howBuilt;
    const mark = element("span", "brand-mark brand-mark-small", PRODUCT.brandMark);
    mark.setAttribute("aria-hidden", "true");
    brand.append(mark, element("span", undefined, "How I Built This"));
    const refresh = element("button", "mark-prompts-refresh", "Refresh");
    refresh.type = "button";
    refresh.addEventListener("click", () => void this.start());
    header.append(brand, refresh);

    const intro = element("section", "mark-prompts-intro");
    intro.append(
      element("p", "eyebrow", "Public, evergreen history"),
      element("h1", undefined, "Mark prompts"),
      element(
        "p",
        "mark-prompts-lede",
        "A public, chronological record of what Mark asked, what followed, and whether the resulting work is still open.",
      ),
      this.renderStats(),
    );

    const liveStatus = element("p", "visually-hidden");
    liveStatus.id = "mark-prompts-status";
    liveStatus.setAttribute("aria-live", "polite");
    liveStatus.setAttribute("aria-atomic", "true");

    main.append(
      header,
      intro,
      this.renderFilters(),
      liveStatus,
      this.renderPromptList(),
    );
    this.root.replaceChildren(main);
    if (announcement) {
      requestAnimationFrame(() => {
        liveStatus.textContent = announcement;
        if (focusSearch) {
          main.querySelector<HTMLInputElement>("#mark-prompts-search")?.focus();
        }
      });
    }
  }

  private renderStats(): HTMLElement {
    const stats = element("div", "mark-prompts-stats");
    const total = element("div", "mark-prompts-total");
    total.append(
      element("strong", undefined, this.stats.total.toLocaleString()),
      element("span", undefined, "prompts recorded"),
    );
    stats.append(total, this.renderChart(this.stats.byDay.slice(-30)));
    return stats;
  }

  private renderChart(days: MarkPromptDay[]): HTMLElement {
    const figure = element("figure", "mark-prompts-chart");
    const caption = element(
      "figcaption",
      undefined,
      days.length === 0 ? "Prompts per day" : "Prompts per day · latest 30 active days",
    );
    figure.append(caption);
    if (days.length === 0) {
      figure.append(element("p", "mark-prompts-chart-empty", "No prompts recorded yet."));
      return figure;
    }
    const maximum = Math.max(...days.map(({ count }) => count));
    const list = element("ol", "mark-prompts-bars");
    days.forEach((day) => {
      const item = element("li", "mark-prompts-bar-item");
      const bar = element("span", "mark-prompts-bar");
      bar.style.setProperty(
        "--mark-prompt-bar-height",
        `${Math.max(8, Math.round((day.count / maximum) * 100))}%`,
      );
      bar.setAttribute("role", "img");
      bar.setAttribute(
        "aria-label",
        `${day.date}: ${day.count} ${day.count === 1 ? "prompt" : "prompts"}`,
      );
      item.append(
        element("span", "mark-prompts-bar-count", String(day.count)),
        bar,
        element("span", "mark-prompts-bar-date", chartLabel(day)),
      );
      list.append(item);
    });
    figure.append(list);
    return figure;
  }

  private renderFilters(): HTMLElement {
    const section = element("section", "mark-prompts-controls");
    section.setAttribute("aria-label", "Filter prompt history");

    const tasks = element("fieldset", "mark-prompts-task-filter");
    const legend = element("legend", undefined, "Agent tasks");
    const taskActions = element("div", "mark-prompts-task-actions");
    const selectAll = element("button", undefined, "Select all");
    selectAll.type = "button";
    selectAll.addEventListener("click", () => {
      this.selectedTaskIDs = new Set(
        this.sources.map(({ taskID }) => taskID),
      );
      void this.reloadTimeline();
    });
    const clear = element("button", undefined, "Clear");
    clear.type = "button";
    clear.addEventListener("click", () => {
      this.selectedTaskIDs.clear();
      void this.reloadTimeline();
    });
    taskActions.append(selectAll, clear);
    const taskChoices = element("div", "mark-prompts-task-choices");
    this.sources.forEach((source) => {
      const label = element("label", "mark-prompts-task-choice");
      const input = element("input");
      input.type = "checkbox";
      input.value = source.taskID;
      input.checked = this.selectedTaskIDs.has(source.taskID);
      input.addEventListener("change", () => {
        if (input.checked) this.selectedTaskIDs.add(source.taskID);
        else this.selectedTaskIDs.delete(source.taskID);
        void this.reloadTimeline();
      });
      label.title = `Task ${source.taskID}`;
      label.append(
        input,
        element("span", undefined, source.taskTitle),
        element("small", undefined, source.count.toLocaleString()),
      );
      taskChoices.append(label);
    });
    tasks.append(legend, taskActions, taskChoices);

    const searchLabel = element("label", "mark-prompts-search-label");
    searchLabel.htmlFor = "mark-prompts-search";
    searchLabel.append(element("span", undefined, "Search loaded prompts"));
    const search = element("input", "mark-prompts-search");
    search.id = "mark-prompts-search";
    search.type = "search";
    search.placeholder = "Prompt, result, ID, or issue";
    search.value = this.searchQuery;
    search.addEventListener("input", () => {
      this.searchQuery = search.value;
      this.replaceList();
    });
    searchLabel.append(search);

    const states = element("fieldset", "mark-prompts-state-filter");
    const stateLegend = element("legend", undefined, "Statuses");
    const stateActions = element("div", "mark-prompts-state-actions");
    const selectAllStates = element("button", undefined, "Select all");
    selectAllStates.type = "button";
    selectAllStates.addEventListener("click", () => {
      this.selectedStates = new Set(reviewStates);
      this.renderDashboard("All statuses selected.");
    });
    const clearStates = element("button", undefined, "Clear");
    clearStates.type = "button";
    clearStates.addEventListener("click", () => {
      this.selectedStates.clear();
      this.renderDashboard("No statuses selected.");
    });
    stateActions.append(selectAllStates, clearStates);
    const stateChoices = element("div", "mark-prompts-state-choices");
    reviewStates.forEach((reviewState) => {
      const label = element("label", "mark-prompts-state-choice");
      const input = element("input");
      input.type = "checkbox";
      input.value = reviewState;
      input.checked = this.selectedStates.has(reviewState);
      input.addEventListener("change", () => {
        if (input.checked) this.selectedStates.add(reviewState);
        else this.selectedStates.delete(reviewState);
        this.replaceList();
      });
      label.append(input, element("span", undefined, reviewLabels[reviewState]));
      stateChoices.append(label);
    });
    states.append(stateLegend, stateActions, stateChoices);

    const orderLabel = element("label", "mark-prompts-order-label");
    orderLabel.htmlFor = "mark-prompts-order";
    orderLabel.append(element("span", undefined, "Timeline order"));
    const order = element("select", "mark-prompts-order");
    order.id = "mark-prompts-order";
    for (const [value, label] of [
      ["desc", "Newest first"],
      ["asc", "Oldest first"],
    ] as Array<[MarkPromptOrder, string]>) {
      const option = element("option", undefined, label);
      option.value = value;
      option.selected = this.order === value;
      order.append(option);
    }
    order.addEventListener("change", () => {
      this.order = order.value as MarkPromptOrder;
      void this.reloadTimeline();
    });
    orderLabel.append(order);

    section.append(tasks, searchLabel, states, orderLabel);
    return section;
  }

  private selectedQueryTaskIDs(): string[] {
    const currentSourceIDs = new Set(
      this.sources.map(({ taskID }) => taskID),
    );
    const selectedCurrentIDs = [...this.selectedTaskIDs]
      .filter((taskID) => currentSourceIDs.has(taskID))
      .sort();
    if (
      selectedCurrentIDs.length === currentSourceIDs.size
      && [...currentSourceIDs].every((taskID) => (
        this.selectedTaskIDs.has(taskID)
      ))
    ) {
      return [];
    }
    return selectedCurrentIDs;
  }

  private async reloadTimeline(): Promise<void> {
    const generation = ++this.loadGeneration;
    this.loadingOlder = false;
    if (this.selectedTaskIDs.size === 0) {
      this.prompts = [];
      this.stats = { total: 0, byDay: [] };
      this.nextOffset = null;
      this.renderDashboard("No agent tasks selected.");
      return;
    }
    this.root.setAttribute("aria-busy", "true");
    try {
      const page = await this.repository.load({
        taskIDs: this.selectedQueryTaskIDs(),
        order: this.order,
      });
      if (generation !== this.loadGeneration) return;
      this.prompts = page.prompts;
      this.stats = page.stats;
      const allPreviousSourcesSelected =
        this.selectedTaskIDs.size === this.sources.length
        && this.sources.every(({ taskID }) => (
          this.selectedTaskIDs.has(taskID)
        ));
      const currentSourceIDs = new Set(
        page.sources.map(({ taskID }) => taskID),
      );
      this.selectedTaskIDs = allPreviousSourcesSelected
        ? currentSourceIDs
        : new Set(
          [...this.selectedTaskIDs].filter((taskID) => (
            currentSourceIDs.has(taskID)
          )),
        );
      this.sources = page.sources;
      this.nextOffset = page.nextOffset;
      this.renderDashboard(
        `${this.stats.total} ${this.stats.total === 1 ? "prompt" : "prompts"} in this timeline.`,
      );
    } catch {
      if (generation !== this.loadGeneration) return;
      this.renderUnavailable();
    } finally {
      if (generation === this.loadGeneration) {
        this.root.removeAttribute("aria-busy");
      }
    }
  }

  private filteredPrompts(): MarkPrompt[] {
    const query = this.searchQuery.trim().toLocaleLowerCase();
    return this.prompts.filter((prompt) => {
      if (!this.selectedStates.has(prompt.reviewState)) {
        return false;
      }
      if (!query) return true;
      return [
        prompt.id,
        prompt.summary,
        prompt.abbreviatedText,
        prompt.fullText,
        prompt.resultSummary ?? "",
        prompt.duplicateOfPromptID ?? "",
        prompt.source.taskTitle,
        ...prompt.links.flatMap(({ label, status, url }) => [
          label,
          status,
          url,
        ]),
      ].some((value) => value.toLocaleLowerCase().includes(query));
    });
  }

  private replaceList(): void {
    const current = this.root.querySelector<HTMLElement>("#mark-prompts-list-region");
    current?.replaceWith(this.renderPromptList());
  }

  private renderPromptList(): HTMLElement {
    const region = element("section", "mark-prompts-list-region");
    region.id = "mark-prompts-list-region";
    region.setAttribute("aria-label", "Prompt history");
    const filtered = this.filteredPrompts();
    const heading = element("div", "mark-prompts-list-heading");
    heading.append(
      element("h2", undefined, "Prompt history"),
      element(
        "p",
        undefined,
        `${filtered.length} shown · ${this.prompts.length} loaded · ${this.stats.total} total`,
      ),
    );
    region.append(heading);
    if (filtered.length === 0) {
      region.append(element(
        "p",
        "mark-prompts-empty",
        this.prompts.length === 0
          ? "No real Mark prompts have been imported yet."
          : "No loaded prompt matches these filters.",
      ));
    } else {
      const list = element("div", "mark-prompts-list");
      filtered.forEach((prompt) => list.append(this.renderPrompt(prompt)));
      region.append(list);
    }
    if (this.nextOffset !== null) {
      const load = element(
        "button",
        "mark-prompts-load",
        this.loadingOlder ? "Loading…" : "Load older prompts",
      );
      load.type = "button";
      load.disabled = this.loadingOlder;
      load.addEventListener("click", () => void this.loadOlder());
      region.append(load);
    }
    return region;
  }

  private renderPrompt(prompt: MarkPrompt): HTMLElement {
    const article = element(
      "article",
      `mark-prompt-card state-${prompt.reviewState}`,
    );
    article.dataset.promptId = prompt.id;
    const meta = element("div", "mark-prompt-meta");
    const occurredAt = element("time", "mark-prompt-time");
    occurredAt.dateTime = prompt.occurredAt;
    occurredAt.textContent = formatTimestamp(prompt.occurredAt);
    meta.append(
      occurredAt,
      element(
        "span",
        "mark-prompt-source",
        prompt.source.taskTitle,
      ),
      element(
        "span",
        `mark-prompt-state state-${prompt.reviewState}`,
        reviewLabels[prompt.reviewState],
      ),
    );
    const title = element("h3", undefined, prompt.summary);
    article.append(
      meta,
      title,
      element("p", "mark-prompt-abbreviated", prompt.abbreviatedText),
    );

    const outcome = element("section", "mark-prompt-outcome");
    outcome.append(
      element("h4", undefined, "What happened"),
      element(
        "p",
        undefined,
        prompt.resultSummary
          ?? "Not yet triaged. This prompt has been saved, but no outcome or GitHub work has been linked yet.",
      ),
    );
    if (prompt.duplicateOfPromptID) {
      outcome.append(element(
        "p",
        "mark-prompt-duplicate",
        `Duplicate of ${prompt.duplicateOfPromptID}`,
      ));
    }
    article.append(outcome);

    if (prompt.links.length > 0) {
      const links = element("ul", "mark-prompt-links");
      prompt.links.forEach((item) => {
        const listItem = element("li");
        const anchor = element("a", undefined, item.label);
        anchor.href = item.url;
        anchor.target = "_blank";
        anchor.rel = "noreferrer";
        listItem.append(
          anchor,
          element("span", `mark-prompt-link-status status-${item.status}`, item.status),
        );
        links.append(listItem);
      });
      article.append(links);
    }

    const details = element("details", "mark-prompt-full");
    details.append(
      element("summary", undefined, "Full prompt"),
      element("pre", undefined, prompt.fullText),
    );
    const footer = element("footer", "mark-prompt-footer");
    footer.append(
      element("code", undefined, prompt.id),
      element(
        "span",
        undefined,
        `${prompt.source.taskTitle} · ${prompt.source.taskID}`,
      ),
    );
    article.append(details, footer);
    return article;
  }

  private async loadOlder(): Promise<void> {
    if (this.nextOffset === null || this.loadingOlder) return;
    const generation = this.loadGeneration;
    const offset = this.nextOffset;
    const taskIDs = this.selectedQueryTaskIDs();
    const order = this.order;
    this.loadingOlder = true;
    this.replaceList();
    try {
      const page = await this.repository.load({
        offset,
        taskIDs,
        order,
      });
      if (generation !== this.loadGeneration) return;
      const known = new Set(this.prompts.map(({ id }) => id));
      const newPrompts = page.prompts.filter(({ id }) => !known.has(id));
      this.prompts.push(...newPrompts);
      this.stats = page.stats;
      this.nextOffset = page.nextOffset;
      this.loadingOlder = false;
      this.renderDashboard(
        `${newPrompts.length} older ${newPrompts.length === 1 ? "prompt" : "prompts"} loaded.`,
        true,
      );
    } catch {
      if (generation !== this.loadGeneration) return;
      this.loadingOlder = false;
      this.replaceList();
      const liveStatus = this.root.querySelector<HTMLElement>(
        "#mark-prompts-status",
      );
      if (liveStatus) {
        liveStatus.textContent =
          "Older prompts could not be loaded. Existing prompt data stayed unchanged.";
      }
    }
  }
}

export async function renderMarkPrompts(root: HTMLElement): Promise<void> {
  await new MarkPromptsPage(root).start();
}
